'use client';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { HitType } from '@/modules/common/lib/abtastyStub';
import { DialogClose } from '@radix-ui/react-dialog';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import { Back } from '@/modules/common/components/icons';
import { Heart } from '@/modules/common/components/icons';
import { Close } from '@/modules/common/components/icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/modules/common/components/ui/dialog';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import { useSiteHeaderHeight } from '@/modules/common/hooks/useSiteHeaderHeight';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import CollectionContentDesigner from '@/modules/product/components/collection-designer/CollectionContentDesigner';
import DesignerProfile from '@/modules/product/components/collection-designer/DesignerProfile';
import { useCollectionPageView } from '@/modules/product/hooks/useCollectionPageView';
import { useFilteredCollectionProducts } from '@/modules/product/hooks/useFilteredCollectionProducts';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '@/modules/product/services/data-layer-events/utils';
import { AddToShoppingListButton } from '@/modules/shopping-list/components/AddToShoppingListButton';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { SaveType } from '@/modules/shopping-list/models/shoppingListTextItem.interface';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

type CollectionPageContentDesignerProps = {
  collection: ContentfulCollection;
};

export default function CollectionPageContentDesigner({
  collection,
}: CollectionPageContentDesignerProps) {
  const liveCollection = useContentfulLiveUpdates(collection);
  const isMobile = useIsMobile();
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);
  const { sendFeatureFlagTracking } = useFeatureFlag();

  useCollectionPageView(liveCollection);

  const handleClickBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  const shoppingListData = {
    name: liveCollection.collectionName ?? '',
    key: liveCollection.slug ?? '',
    saveType: SaveType.Collection,
  };
  const wishlistTrackingCallback = () => {
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      method: 'Single',
      type: ShoppingListSaveType.Collection,
      product: {
        slug: liveCollection.slug ?? '',
        title: liveCollection.collectionName ?? '',
      } satisfies CollectionDataLayer,
      where: DataLayerWhere.Collections,
      shoppingListProducts,
      item_list_type: 'Editorial collection',
      item_list_name: liveCollection.collectionName ?? '',
      item_list_id: liveCollection.slug ?? '',
    });

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Collection Saved',
      label: collection.roomType,
    });
  };

  const filteredProducts = useFilteredCollectionProducts(liveCollection.products);
  const skus = filteredProducts?.map((product) => product.sku) || [];

  return (
    <main
      className="mx-auto max-w-[100vw] py-4 lg:max-w-[1920px] lg:pb-10 lg:pt-8"
      data-collection-roomtype={liveCollection.roomType || ''}
      data-collection-type="designer"
    >
      <div className="flex w-full flex-col px-4 md:px-8 lg:flex-row">
        <div className="relative mb-2 flex flex-col gap-5 lg:flex-row lg:gap-8">
          {!isMobile && liveCollection?.newBaseImage?.url && (
            <ZoomCollectionImage collection={liveCollection} />
          )}

          <div className="flex-1">
            <button
              className="flex w-[93px] gap-1 transition-opacity hover:opacity-75 lg:mb-4"
              onClick={handleClickBack}
              data-testid="back-button"
            >
              <Back className="h-[24px] w-[24px]" />
              Back
            </button>

            <div className="flex items-start justify-between py-4">
              <h1 className="text-4xl text-[28px] font-bold leading-tight tracking-tight text-black">
                {liveCollection.collectionName}
              </h1>

              <AddToShoppingListButton
                saveType={ShoppingListSaveType.Collection}
                textLineItem={shoppingListData}
                image={liveCollection.newBaseImage?.url ?? ''}
                className="static h-9 w-9 border border-gray-75 bg-white"
                variant="secondary"
                sku={undefined}
                callbacks={{ afterAdd: wishlistTrackingCallback }}
                data-testid="add-to-shopping-list-button"
              >
                <Heart className="p-1" />
              </AddToShoppingListButton>
            </div>

            {liveCollection.designer?.sys?.id && (
              <DesignerProfile id={liveCollection.designer.sys.id} />
            )}

            {isMobile && liveCollection?.newBaseImage?.url && (
              <ZoomCollectionImage collection={liveCollection} />
            )}

            <CollectionContentDesigner collection={liveCollection} productSkus={skus} />
          </div>
        </div>
      </div>
    </main>
  );
}

const ZoomCollectionImage = ({ collection }: { collection: ContentfulCollection }) => {
  const isMobile = useIsMobile();
  const siteHeaderHeight = useSiteHeaderHeight();

  const BannerImage = (
    <div className="mt-5 lg:mt-0">
      <div
        className="mx-auto mb-1 max-h-[476px] w-fit cursor-zoom-in md:max-h-full lg:sticky lg:max-h-[calc(100vh-220px)]"
        style={!isMobile ? { top: `${siteHeaderHeight}px` } : undefined}
      >
        <div className="max-h-[452px] w-fit max-w-full rounded-xxl md:max-h-full lg:h-[650px] lg:max-h-[calc(100vh-237px)] lg:max-w-[530px] xl:h-[680px] xl:max-w-[610px] xxl:max-w-[640px] 3xl:h-[730px] 3xl:max-w-[720px]">
          <Image
            src={collection.newBaseImage?.url || undefined}
            alt="Collection Banner"
            width={720}
            height={730}
            className="h-auto max-h-[428px] w-full rounded-xxl md:max-h-[calc(100vh-251px)] lg:max-h-full"
          />
          {collection?.photographer && (
            <legend className="mt-2 text-xs text-gray-800">{collection.photographer}</legend>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog>
      <DialogTrigger asChild>{BannerImage}</DialogTrigger>
      <DialogContent
        hideDefaultClose
        className="w-full max-w-3xl overflow-y-auto border-none bg-transparent p-0 lg:max-h-[calc(100vh-4rem)]"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Collection Image</DialogTitle>
          <DialogDescription>Click to zoom in on the collection image.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <DialogClose asChild>
            <Button
              variant="secondary"
              size="icon-m"
              className="absolute right-4 top-4 z-50 size-[48px] h-10 w-10 bg-black-0.6 text-white hover:lg:bg-black-0.2"
            >
              <Close />
            </Button>
          </DialogClose>
          {BannerImage}
        </div>
      </DialogContent>
    </Dialog>
  );
};
